import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';

import GreatDatePicker from '../../../../components/ui/forms/fields/datePicker';
import Select from '../../../../components/ui/forms/fields/select';
import { Button, MenuItem, useTheme } from '@mui/material';
import {
  getReport,
  getProgressReport,
  getInceptionDate
} from '../../../../redux/reports/report.slice';
import { setPageTitle } from '../../../../redux/title/title.slice';
import { Box } from '@mui/system';
import Loader from '../../../../components/ui/loader';

// ✅ FIXED SCHEMA
const schema = yup.object().shape({
  startDate: yup
    .date()
    .nullable()
    .typeError('Start date is required. Please select a valid date.')
    .when('type', {
      is: (value) => value !== 'progress',
      then: (schema) => schema.required('Start date is required. Please select a date.')
    }),

  endDate: yup
    .date()
    .nullable()
    .typeError('End date is required. Please select a valid date.')
    .when('type', {
      is: (value) => value !== 'progress',
      then: (schema) => schema.required('End date is required. Please select a date.')
    }),

  type: yup.string().required('Please select a report type from the dropdown.')
});

function Reports() {
  const theme = useTheme();
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.report.loading);
  const inceptionDate = useSelector((state) => state.report.inceptionDate);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: '',
      startDate: null,
      endDate: null
    }
  });

  const selectedType = useWatch({ control, name: 'type' });

  useEffect(() => {
    dispatch(setPageTitle('Reports'));
  }, [dispatch]);

  useEffect(() => {
    if (selectedType) {
      dispatch(getInceptionDate(selectedType));
    }
  }, [selectedType, dispatch]);

  // ✅ FIXED: safer date comparisons
  useEffect(() => {
    if (inceptionDate) {
      const inception = moment(inceptionDate).startOf('day');

      const start = getValues('startDate');
      const end = getValues('endDate');

      if (start && moment(start).isBefore(inception)) {
        setValue('startDate', null);
      }

      if (end && moment(end).isBefore(inception)) {
        setValue('endDate', null);
      }
    }
  }, [inceptionDate, setValue, getValues]);

  // ✅ FIXED: format only here
  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      startDate: data.startDate ? moment(data.startDate).format('YYYY-MM-DD') : null,
      endDate: data.endDate ? moment(data.endDate).format('YYYY-MM-DD') : null
    };

    if (data.type === 'progress') {
      dispatch(getProgressReport(formattedData));
    } else {
      dispatch(getReport(JSON.stringify(formattedData)));
    }
  };

  const minDate = inceptionDate
    ? moment(inceptionDate).toDate() // ✅ pass real Date, not string
    : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Select
          name="type"
          label="Report"
          control={control}
          error={errors?.type}
          helperText={errors?.type?.message}
          sx={{
            marginRight: theme.spacing(2)
          }}>
          <MenuItem value={'progress'}>Progress Report</MenuItem>
          <MenuItem value={'ratings'}>Rating</MenuItem>
          <MenuItem value={'savedApplications'}>Saved Applications</MenuItem>
          <MenuItem value={'speakToAConsultant'}>Speak To Consultant</MenuItem>
          <MenuItem value={'submissions'}>Submissions</MenuItem>
        </Select>

        <GreatDatePicker
          label="Start Date"
          name="startDate"
          control={control}
          error={errors?.startDate}
          minDate={minDate}
          sx={{
            marginRight: theme.spacing(2)
          }}
        />

        <GreatDatePicker
          label="End Date"
          name="endDate"
          control={control}
          error={errors?.endDate}
          minDate={minDate}
          sx={{
            marginRight: theme.spacing(2)
          }}
        />

        <Button
          type="submit"
          sx={{
            width: '100%',
            mt: theme.spacing(1),
            backgroundColor: theme.palette.accent.main,
            color: '#fff',
            '&:hover': {
              backgroundColor: '#904622',
              color: theme.palette.accent.main
            }
          }}>
          Download
          {loading && <Loader loading={true} message="Loading ..." />}
        </Button>
      </form>
    </Box>
  );
}

export default Reports;
